import React, { useMemo, useState } from "react";
import { useFormatDate } from "hooks";
import { useHistory } from "react-router-dom";
import { useLineItemsQuery } from "generated/graphql";
import { getBUFromLocalStorage } from "helpers/storage";
import { LineItems_lineItems } from "api/Medicine/types/LineItems";
import { Flex, Spacer, EmptyState, Table, InputText, LoadingOverlay } from "components";
import { MEDICINE_DETAILS } from "constants/Routes";
import { TABLE_IDS } from "constants/Interface";
import { Column, Row } from "react-table";
import { DateTime } from "luxon";
import styles from "./styles.module.scss";

export const TEST_IDS = {
  NO_MEDICINES_TEXT: "no-medicines-text",
  NO_MATCHING_TEXT: "no-matching-text",
  SEARCH_INPUT: "medicine-search-input",
};

const COLUMNS_IDS = {
  MEDICINE_NAME: "name",
  BATCH_NUMBER: "batchNumber",
  PURCHASE_DATE: "purchaseDate",
  EXPIRY_DATE: "expiryDate",
  AMOUNT: "amount",
  SKU: "sku",
};

const tableId = TABLE_IDS["MEDICINES_TABLE"];

const filterMedicineData = (data: LineItems_lineItems[], searchKey: string): LineItems_lineItems[] => {
  const isValueMatch = (value: string): boolean => value?.toLowerCase().includes(searchKey.toLowerCase());

  return data.filter(
    (item) => isValueMatch(item.stockItem.name) || isValueMatch(item.batchNumber) || isValueMatch(item.sku),
  );
};

export const MedicinesTable: React.FC = () => {
  const [searchKey, changeSearchKey] = useState<string>("");
  const history = useHistory();

  const handleClickCell = React.useCallback(
    ({ stockItem }: LineItems_lineItems) => {
      history.push(`${MEDICINE_DETAILS}/${stockItem.id}`);
    },
    [history],
  );

  const businessUnitId: number = getBUFromLocalStorage();

  const { data, loading } = useLineItemsQuery({
    variables: {
      businessUnitId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const { getFormatDate } = useFormatDate();
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    changeSearchKey(event.target.value);
  };

  const columns: Column<LineItems_lineItems>[] = useMemo(
    () => [
      {
        Header: "Medicine name",
        accessor: ({ stockItem }: LineItems_lineItems): string => {
          return stockItem.name;
        },
        onCellClick: handleClickCell,
        id: COLUMNS_IDS.MEDICINE_NAME,
        width: 300,
        sortType: sortingFunc,
      },
      {
        Header: "Batch number",
        onCellClick: handleClickCell,
        accessor: ({ batchNumber }: LineItems_lineItems): string => {
          return batchNumber || "\u2014";
        },
        id: COLUMNS_IDS.BATCH_NUMBER,
        width: 200,
      },
      {
        Header: "Purchase date",
        onCellClick: handleClickCell,
        accessor: ({ purchaseDate }: LineItems_lineItems): string => {
          return getFormatDate(purchaseDate);
        },
        id: COLUMNS_IDS.PURCHASE_DATE,
        width: 160,
        useSortBy: true,
        sortType: sortDates,
      },
      {
        Header: "Expiry date",
        onCellClick: handleClickCell,
        accessor: ({ expiryDate }: LineItems_lineItems): string => {
          return getFormatDate(expiryDate);
        },
        id: COLUMNS_IDS.EXPIRY_DATE,
        width: 160,
        sortType: sortDates,
      },
      {
        Header: "Amount",
        onCellClick: handleClickCell,
        accessor: ({ totalVolume, quantity, originalVolume, quantityUnits }: LineItems_lineItems): string => {
          const currentQty = totalVolume || "-";
          const originalBatchQty = quantity && originalVolume ? quantity * originalVolume : "-";
          const qtyUnits = quantityUnits || "";

          return `${currentQty}/${originalBatchQty} ${qtyUnits}`;
        },
        id: COLUMNS_IDS.AMOUNT,
        width: 160,
      },
      {
        Header: "GTIN",
        onCellClick: handleClickCell,
        accessor: ({ sku }: LineItems_lineItems): string => {
          return sku || "\u2014";
        },
        id: COLUMNS_IDS.SKU,
        width: 160,
      },
    ],
    [getFormatDate, handleClickCell],
  );

  const medicines = React.useMemo(() => (data?.lineItems || []) as LineItems_lineItems[], [data?.lineItems]);
  const filteredMedicines: LineItems_lineItems[] = useMemo(
    () => filterMedicineData(medicines, searchKey),
    [medicines, searchKey],
  );
  const tableData: LineItems_lineItems[] = useMemo(() => filteredMedicines, [filteredMedicines]);
  const isEmptyStateVisible = !medicines.length;

  if (isEmptyStateVisible) {
    return <EmptyState page="MEDICINES" />;
  }

  return (
    <Flex container>
      <Flex item itemGutter xs={12} m={6} l={3} className={styles.search_medicine_input}>
        <InputText
          name="medicines_search"
          onChange={handleSearchInputChange}
          placeholder="Search for a medicine"
          type="search"
          value={searchKey}
        />
      </Flex>
      <Spacer baselineHeight={1} />
      {loading && <LoadingOverlay />}
      <Flex item itemGutter xs={12}>
        <Table<LineItems_lineItems>
          tableId={tableId}
          initialSortBy={[{ id: COLUMNS_IDS.MEDICINE_NAME }]}
          emptyTableText="No matching medicines"
          columns={columns}
          data={tableData}
        />
      </Flex>
    </Flex>
  );
};

const sortDates = (rowA: Row<LineItems_lineItems>, rowB: Row<LineItems_lineItems>, columnId: string): number => {
  const firstDate = DateTime.fromFormat(rowA.values[columnId], "dd/MM/yyyy");
  const secondDate = DateTime.fromFormat(rowB.values[columnId], "dd/MM/yyyy");

  if (firstDate > secondDate) {
    return 1;
  }

  if (firstDate < secondDate) {
    return -1;
  }
  return 0;
};

const sortingFunc = (rowA: Row<LineItems_lineItems>, rowB: Row<LineItems_lineItems>, columnId: string): number => {
  const firstValue = rowA.values[columnId].toLowerCase();
  const secondValue = rowB.values[columnId].toLowerCase();

  if (firstValue > secondValue) {
    return 1;
  }

  if (firstValue < secondValue) {
    return -1;
  }
  return 0;
};
